/**
 * Canonical data model for engagement project goals.
 *
 * This is the single source of truth shared between backend and frontend.
 * It is stored at KV key: company:{companyId}:engagement_context
 *
 * DO NOT flatten this into text fields. All editing must preserve structure.
 */

export interface CurrentSystem {
  name: string;
  usage: 'primary' | 'secondary' | 'fallback';
  notes: string;
}

export interface PainPoint {
  description: string;
  category: 'process' | 'technology' | 'people' | 'data';
  severity: 'high' | 'medium' | 'low';
  source: 'sales_notes' | 'sow' | 'hlo' | 'inferred';
}

export interface Objective {
  description: string;
  type: 'efficiency' | 'reliability' | 'experience' | 'cost' | 'compliance' | 'scalability';
  priority: 'high' | 'medium' | 'low';
  source: 'sales_notes' | 'sow' | 'hlo';
}

export interface SuccessMetric {
  name: string;
  target: string;
  baseline: string;
  confidence: 'high' | 'medium' | 'low';
  source: 'sales_notes' | 'sow' | 'inferred';
}

export interface TimelinePhase {
  name: string;
  scope: string;
  target_date: string;
}

export interface Timeline {
  target_go_live: string;
  phases: TimelinePhase[];
  constraints: string[];
}

export interface ConfidenceSummary {
  overall: 'high' | 'medium' | 'low';
  warnings: string[];
}

export interface ProjectGoals {
  current_systems: CurrentSystem[];
  pain_points: PainPoint[];
  objectives: Objective[];
  success_metrics: SuccessMetric[];
  timeline: Timeline;
  confidence_summary: ConfidenceSummary;
  schema_version: 'v1';
}

/** Envelope stored in KV at company:{companyId}:engagement_context */
export interface EngagementContext {
  project_goals: ProjectGoals;
  extractedAt: string;
  source: string;
  tokensUsed?: number;
  inputSources?: {
    hasHlos: boolean;
    hloCount: number;
    hasSalesNotes: boolean;
    hasSow: boolean;
  };
}

/** Create an empty but structurally valid ProjectGoals */
export function emptyProjectGoals(): ProjectGoals {
  return {
    current_systems: [],
    pain_points: [],
    objectives: [],
    success_metrics: [],
    timeline: {
      target_go_live: '',
      phases: [],
      constraints: [],
    },
    confidence_summary: {
      overall: 'low',
      warnings: ['No upstream data available — please populate manually'],
    },
    schema_version: 'v1',
  };
}
